import React, { useMemo, useRef } from "react";
import { useParams } from "react-router";
import { getQueryParamsFromPath } from "../../../utils/getQueryParamsFromPath";
import { useGetProductsInfiniteQuery } from "../../../providers/products";
import { useActiveOrMainShippingZone } from "../../../providers/shippingZones";
import { useGetCategoryByIdQuery } from "../../../providers/categories";
import TemplatePage from "../template";
import styles from "./category.module.scss";
import classNames from "classnames/bind";
import { useSearchParams } from "../../../hooks/useSearchParams";
import { AdditionalFeature, Product } from "@core/types";
import useGetProductsQuery from "../../../providers/products/useGetProductsQuery";

const cx = classNames.bind(styles);

interface RouteParams {
  id: string;
  name: string;
  type: string;
}

const defaultParams = {
  min_price: 0.1,
};

const CategoryPage = () => {
  const params = useParams<RouteParams>();
  const infiniteRef = useRef<HTMLIonInfiniteScrollElement | null>(null);
  const categoryEnabled = params.id !== "0";
  const { data: category, isLoading: isCategoryLoading } = useGetCategoryByIdQuery(params.id!, {
    enabled: categoryEnabled,
  });
  const { activeShippingZone, isLoading: isActiveShippingZoneLoading } = useActiveOrMainShippingZone();

  const { get } = useSearchParams();
  const filterValue = get("additionalProductFilter");
  const currentFilter = filterValue === null ? undefined : filterValue;

  const queryParams = useMemo(() => {
    return getQueryParamsFromPath(params);
  }, [params]);

  const fetchProductEnabled = !!queryParams && !!activeShippingZone.id;

  const {
    data: fetchedProducts = {
      pages: [],
      pageParams: [],
    },
    fetchNextPage,
    hasNextPage,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
    isFetchingNextPage,
  } = useGetProductsInfiniteQuery({
    options: {
      enabled: fetchProductEnabled,
    },
    params: {
      ...defaultParams,
      ...queryParams,
      pageNumber: "1",
      pageSize: "12",
      zoneId: activeShippingZone.id,
      additionalProductFilter: currentFilter,
    },
  });

  const categoryName = category?.name || params.name;

  const enableCategoryProducts = !!category?.id && !!activeShippingZone.id;

  const { data: categoryProductsResults, isLoading: isCategoryProductsLoading } = useGetProductsQuery(
    {
      zoneId: activeShippingZone.id,
      categoryId: category?.id,
    },
    {
      enabled: enableCategoryProducts,
    },
  );

  const fetchedProductsList = useMemo(() => {
    const productsMap = new Map<string | number, Product>();

    const products = fetchedProducts?.pages.length
      ? fetchedProducts?.pages.flatMap((page) => page?.products)
      : categoryProductsResults?.products;

    products?.forEach((product) => {
      if (product && !productsMap.has(product.id)) {
        productsMap.set(product.id, product);
      }
    });

    return Array.from(productsMap.values());
  }, [fetchedProducts, categoryProductsResults]);

  const additionalFilters = useMemo(() => {
    const productsMap = new Map<string | number, AdditionalFeature>();

    const additionalFeatures = fetchedProducts?.pages.length
      ? fetchedProducts?.pages.flatMap((page) => page?.additionalFeatures)
      : categoryProductsResults?.additionalFeatures;

    additionalFeatures?.forEach((additionalFeature) => {
      if (additionalFeature && !productsMap.has(additionalFeature.additionalFeature)) {
        productsMap.set(additionalFeature.additionalFeature, additionalFeature);
      }
    });

    return Array.from(productsMap.values());
  }, [fetchedProducts, categoryProductsResults]);

  const isCategoryEnabledAndLoading = categoryEnabled && isCategoryLoading;
  const isInfiniteEnabledAndLoading = fetchProductEnabled && isProductsLoading;
  const isCategoryProductEnabledAndLoading = enableCategoryProducts && isCategoryProductsLoading;

  const isLoading =
    isCategoryEnabledAndLoading ||
    isInfiniteEnabledAndLoading ||
    isCategoryProductEnabledAndLoading ||
    isActiveShippingZoneLoading;
  const productsList = fetchedProductsList?.length ? fetchedProductsList : categoryProductsResults?.products || [];
  const hasProducts = productsList?.length > 0;
  const isInfiniteScrollDisabled = !hasNextPage && !isFetchingNextPage;

  const handleShowMoreClick = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const handleIonInfinite = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
    requestAnimationFrame(() => {
      infiniteRef.current?.complete();
    });
  };

  const base64Image = category?.image?.base64 || "";

  return (
    <TemplatePage
      title={categoryName}
      filters={additionalFilters}
      description={category?.description ?? ""}
      imageSrc={category?.image?.src ?? ""}
      base64Image={base64Image}
      products={productsList}
      hasProducts={hasProducts}
      isLoading={isLoading}
      isRefetchingProducts={isProductsFetching && !isProductsLoading}
      infiniteRef={infiniteRef}
      isInfiniteScrollDisabled={isInfiniteScrollDisabled}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onIonInfinite={handleIonInfinite}
      onShowMoreClick={handleShowMoreClick}
      className={cx("categoryImage")}
    />
  );
};

export default CategoryPage;
